import React, { useEffect, useState } from "react";
import { Input, LabelText, Text, FlexContainer, Img, InputWrapper, PText } from "../../design";
import CvvIcon from "../../assets/imgs/cvv.svg";
import { COUNTRIES_LIST } from "../../appconstants";
import { Controller } from "react-hook-form";
import NumberFormat from "react-number-format";
import Select from "react-select";
import { ErrorMessage } from "@hookform/error-message";
import { Card, Segmented } from "antd";
import { IdcardOutlined, UserOutlined } from "@ant-design/icons";
import { MethodIcon } from "./helper";
import { getLocationDetails } from "../../api";
import { State as ListofStates } from 'country-state-city';

const postalCodes = require("postal-codes-js");

const SupportingFields = ({
  control,
  errors,
  setValue,
  watch,
  selectedBank,
  reset,
  filteredSegmentOptions,
}) => {
  const [postalConfig, setPostalConfig] = useState({
    label: "Pincode",
    placeholder: "Area Pincode",
    maxLength: 6,
  });
  const [defaultCountry, setDefaultCountry] = useState(null);
  const [statesList, setStatesList] = useState([]);
  const [pinCode, setPinCode] = useState(null);

  const isCard = selectedBank?.type === 'CARD';

  const title = isCard
    ? selectedBank?.card?.brand
    : selectedBank?.bank?.bankName;

  const subText = isCard
    ? `${selectedBank?.card?.expiryMonth} / ${selectedBank?.card?.expiryYear}`
    : selectedBank?.bank?.routingNumber;

  const lastFour = isCard
    ? selectedBank?.card?.lastFour
    : selectedBank?.bank?.lastFour;

  const accHolderName = isCard
    ? selectedBank?.card?.name
    : selectedBank?.bank?.name;

  useEffect(() => {
    if (defaultCountry?.code === "IN") {
      setPostalConfig({ label: "Pincode", placeholder: "Area Pincode", maxLength: 6 });
    } else {
      setPostalConfig({ label: "Zipcode", placeholder: "Area Zipcode", maxLength: 5 });
    }
  }, [defaultCountry]);

    useEffect(() => {
      if (selectedBank) {
        initAddress();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedBank]);
  
    const initAddress = async () => {
      try {
        const prefillCountry = selectedBank?.card?.billingCountry || selectedBank?.bank?.billingCountry;

        if (prefillCountry) {
          const countryStates = ListofStates.getStatesOfCountry(prefillCountry);
  
          setDefaultCountry({
            name: prefillCountry,
            code: prefillCountry,
          });
  
          setStatesList(countryStates);
          reset({
            address: selectedBank?.card?.billingStreet || selectedBank?.bank?.billingStreet || '',
            city: selectedBank?.card?.billingCity || selectedBank?.bank?.billingCity || '',
            state: selectedBank?.card?.billingState || selectedBank?.bank?.billingState || '',
            postalCode: selectedBank?.card?.billingPostalCode || selectedBank?.bank?.billingPostalCode || '',
            country: prefillCountry,
          });
  
          return;
        }
  
        // Case 2: Detect IP country
        const data = await getLocationDetails();
  
        const detectedCountry = data?.country || 'US';
        const detectedPostal = data?.postal || '';
  
        setDefaultCountry({
          name: data?.country_name,
          code: detectedCountry,
        });
  
        setPinCode(detectedPostal);
  
        const countryStates = ListofStates.getStatesOfCountry(detectedCountry);
        setStatesList(countryStates);
  
        reset({
          country: detectedCountry,
          postalCode: detectedPostal,
        });
  
      } catch (e) {
        console.error(e);
  
        // fallback safe default
        const fallback = 'US';
  
        setDefaultCountry({ name: 'United States', code: fallback });
        setStatesList(ListofStates.getStatesOfCountry(fallback));
  
        reset({
          country: fallback,
        });
      }
    };

  const handleCountryChange = (code) => {
    const countryStates = ListofStates.getStatesOfCountry(code);

    setDefaultCountry({
      name: code,
      code,
    });

    setStatesList(countryStates);

    setValue('billstate', '');
    setValue('country', code);
  };

  const validatePostal = (value) => {
    const code = watch()?.country;
    const result = postalCodes.validate(code, value);
    return result === true || result;
  };

  const stateOptions = statesList?.map((item) => {
    return { label: item?.name, value: item?.isoCode }
  });

  const rowProps = { xs: "100%", md: "100%", gap: "5%" };
  const colProps = { xs: "100%", flexDirection: "column" };

  const inputStyle = {
    background: "white",
    borderColor: "#CCCCCC",
    width: "100%",
    height: "41px",
    paddingLeft: "15px",
  };

  const labelStyle = { color: "black" };

  const selectStyles = {
    container: (base) => ({ ...base, width: "100%", marginBottom: "8px", paddingTop: '6px' }),
    control: (base) => ({
      ...base,
      height: "41px",
      minHeight: "41px",
      border: "1px solid #CCCCCC",
      boxShadow: "none",
    }),
  };

  const customContainerStyle = {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    justifyContent: 'center',
    alignContent: 'center',
    alignItems: 'center',
    borderStyle: 'solid',
    borderWidth: '1px',
    borderColor: '#CCCCCC',
    borderRadius: '5px',
    // marginTop: '8px',
    // marginBottom: '8px',
    width: '100%'
  }

  const Error = ({ name }) => (
    <ErrorMessage
      errors={errors}
      name={name}
      render={({ message }) => (
        <Text fontSize="12px" marginTop="4px" marginBottom="0px" color="#ff1e1e">
          {message}
        </Text>
      )}
    />
  );

  const getInputStyle = (name) => ({
    ...inputStyle,
    borderColor: errors?.[name] ? '#ff1e1e' : '#CCCCCC',
  });


  const countryOptions = COUNTRIES_LIST.map((item) => {
    return { label: item?.name, value: item?.code }
  });

  return (
    <Card
      className="mt-3"
      title={
        <div className="d-flex align-items-center mt-2 mb-2" style={{ gap: '10px' }}> 
          <div className="bank-icon-placeholder d-flex flex-column justify-content-center align-items-center"  style={{ flexShrink: 0  }}>
            {MethodIcon(selectedBank?.type)}
          </div>
          <div>
            {title}
          </div>
        </div>
      }
    >
      <FlexContainer>
        <FlexContainer {...rowProps}>
          <FlexContainer {...colProps} md="28%">
            <LabelText style={labelStyle}>
              {isCard ?  '# Card Number' : '# Account Number'}
            </LabelText>
            <PText>**** ***** {lastFour}</PText>
          </FlexContainer>
          <FlexContainer {...colProps} md="30%">
            <LabelText style={labelStyle}>
              <UserOutlined /> {isCard ? 'Card Holder Name' : 'Account Holder Name'}
            </LabelText>
            <PText>{accHolderName || '-'}</PText>
          </FlexContainer>
          <FlexContainer {...colProps} md="25%">
            <LabelText style={labelStyle}>
              <IdcardOutlined /> {isCard ?  'Expiry Date' : 'Routing Number'}
            </LabelText>
            <PText>{subText}</PText>
          </FlexContainer>
        </FlexContainer>
        {selectedBank?.type === 'BANK' && filteredSegmentOptions?.length > 0 && (
          <FlexContainer {...rowProps} className="mt-4">
            <Controller
              name="bankType"
              control={control}
              rules={{ required: "Select bank type" }}
              render={({ field }) => (
                <Segmented
                  options={filteredSegmentOptions?.map((item) => ({
                    label: (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Img src={item.icon} />
                        <span>{item.label}</span>
                      </div>
                    ),
                    value: item.key,
                  }))}
                  value={watch()?.bankType || ''}
                  onChange={(value) => setValue('bankType', value)}
                  className="full-width-segmented"
                />
              )}
            />
            <Error name="bankType" />
          </FlexContainer>
        )}
        <FlexContainer {...rowProps}>
          <FlexContainer {...colProps} md="100%">
            <LabelText style={labelStyle}>
              Address <span style={{ color: "red" }}>*</span>
            </LabelText>
            <Controller
              name="address"
              control={control}
              rules={{ required: "Street Address is required" }}
              color={getInputStyle('address')}
              as={<Input style={inputStyle} />}
            />
            <Error name="address" />
          </FlexContainer>
        </FlexContainer>

        {/* State + Postal */}
        <FlexContainer {...rowProps} className="d-flex align-items-center justify-content-center">
          <FlexContainer {...colProps} md="47.5%">
            <LabelText style={labelStyle}>
              City <span style={{ color: "red" }}>*</span>
            </LabelText>
            <Controller
              color={getInputStyle('city')}
              name="city"
              control={control}
              rules={{ required: "City is required" }}
              as={<Input style={inputStyle} />}
            />
            <Error name="city" />
          </FlexContainer>

          <FlexContainer {...colProps} md="47.5%">
            <LabelText style={labelStyle}>
              State <span style={{ color: "red" }}>*</span>
            </LabelText>
            <Controller
              name="state"
              color={getInputStyle('state')}
              control={control}
              rules={{ required: "State is required" }}
              render={({ field }) => (
                <Select
                  {...field}
                  placeholder="State"
                  className="px-0 border-0"
                  styles={selectStyles}
                  options={stateOptions}
                  value={stateOptions?.find(
                    (opt) => opt.value === watch()?.state
                  ) || ''}
                  onChange={(option) => {
                    setValue("state", option.value, { 
                      shouldValidate: true,
                      shouldDirty: true 
                    });
                  }}
                />
              )}
            />
            <Error name="state" />
          </FlexContainer>
        </FlexContainer>

        <FlexContainer {...rowProps} className="d-flex align-items-center justify-content-center">
          <FlexContainer {...colProps} md="47.5%">
            <LabelText style={labelStyle}>Country <span style={{ color: 'red' }}>*</span></LabelText>
            <Controller
                marginBottom="0px"
                control={control}
                name="country"
                color={getInputStyle('country')}
                placeholder="Select a country"
                rules={{
                required: 'Country is required'
                }}
                render={({ field }) => (
                <Select
                    {...field}
                    className="px-0 border-0"
                    styles={selectStyles}
                    value={countryOptions?.find(
                      (opt) => opt.value === watch()?.country
                    )}
                    onChange={(option) => {
                      setValue("country", option.value);
                      setValue("state", "");

                      handleCountryChange(option.value);
                    }}
                    placeholder="Country"
                    options={countryOptions}
                />
                )}
            />
            <Error name="country" />
          </FlexContainer>

          <FlexContainer {...colProps} md="47.5%">
            <LabelText style={labelStyle}>
              {postalConfig.label} <span style={{ color: "red" }}>*</span>
            </LabelText>

            <Controller
              name="postalCode"
              control={control}
              rules={{
                required: `${postalConfig.placeholder} is required`,
                validate: validatePostal,
              }}
              customInput={Input}
              as={
                  <NumberFormat
                    maxLength={postalConfig.maxLength}
                    allowNegative={false}
                    decimalScale={0}
                    customInput={Input}
                    style={inputStyle}
                  />
              }
            />
            <Error name="postalCode" />
          </FlexContainer>
        </FlexContainer>
        {selectedBank?.type === 'CARD' && (
          <FlexContainer {...rowProps}>
            <FlexContainer {...colProps} md="50%">
              <LabelText style={{...labelStyle}}>
                CVV <span style={{ color: 'red' }}>*</span>
              </LabelText>
              <InputWrapper style={{...customContainerStyle, width: '100%'}}>
                <Controller
                  defaultValue={''}
                  border="0px !important"
                  marginBottom="0px"
                  marginTop="0px"
                  marginLeft="5px"
                  width='0px'
                  control={control}
                  name="cvv"
                  color={errors.cvv ? "red" : "inherit"}
                  rules={{
                    required: 'CVV is required',
                    pattern: {
                      value: /\d{3,4}/,
                      message: 'Invalid CVV number',
                    },
                  }}
                  as={<NumberFormat
                    style={{...inputStyle}}
                    placeholder="CVV"
                  />}
                  customInput={Input}
                  format="####"
                  mask=""
                  type="password"
                />
                <Img verticalAlign='middle' src={CvvIcon} style={{ paddingRight: '15px' }}/>
              </InputWrapper>
              <Error name="cvv" />
            </FlexContainer>
          </FlexContainer>
        )}
      </FlexContainer>
    </Card>
  );
};

export default SupportingFields;
